import { DynamicForm } from "@shared/components/DynamicForm";
import { GenericTable } from "@shared/components/GenericTable";
import { FormField, FormService } from "@shared/services/FormServices";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  useZoneList,
  useDivisionList,
  useAreaList,
  useSubAreaList,
  usePremisesForBillCycle,
  useMoveBillCycle,
} from "../../hooks";
import { PAGE_SIZE } from "@shared/utils/constants";
import { FIELD_CLASSES } from "@shared/utils/constants";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { TableColumn } from "@shared/types/table";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { AddPremiseToCycle } from "../AddPremiseToCycle";
import { MoveToPremiseDialog } from "./MoveToPremiseDialog";
import { useParams } from "react-router-dom";

interface PremiseTableWithActionsProps {
  premises: string[];
  selectedCategories: string[];
  selectedSubCategories: string[];
  refetchDetail: () => void;
  canPerformAction: boolean
}

export const PremiseTableWithActions: React.FC<
  PremiseTableWithActionsProps
> = ({
  premises,
  selectedCategories,
  selectedSubCategories,
  refetchDetail,
  canPerformAction
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const { id } = useParams();

  // ------------------ STATE ---------------------
  const [premisesSearch, setPremisesSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState<number>();
  const [selectedPremises, setSelectedPremises] = useState<string[]>([]);

  const { mutate: movePremiseToCycle } = useMoveBillCycle();

  // Separate state for form values (for cascading dropdowns)
  const [formFilterValues, setFormFilterValues] = useState({
    zone: [],
    division: [],
    area: [],
    sub_area: [],
  });

  // State for actual query params (only updated on Apply Filters)
  const [queryParams, setQueryParams] = useState({
    page: currentPage,
    search_data: "",
    zone: [],
    division: [],
    area: [],
    sub_area: [],
  });

  // ------------------ HIERARCHY QUERIES ---------------------
  const { data: zoneList = [] } = useZoneList();

  // Fetch divisions based on current form values (for cascading)
  const { data: divisionList = [] } = useDivisionList(formFilterValues.zone[0]);

  // Fetch areas based on current form values (for cascading)
  const { data: areaList = [] } = useAreaList(formFilterValues.division[0]);

  // Fetch sub-areas based on current form values (for cascading)
  const { data: subAreaList = [] } = useSubAreaList(formFilterValues.area[0]);

  // ------------------ PREMISE LIST ---------------------
  // This hook only uses queryParams, which is updated ONLY on Apply Filters click
  const { data: premiseList, refetch } = usePremisesForBillCycle({
    category: selectedCategories,
    subCategory: selectedSubCategories,
    premises: premises,
    area: queryParams.area[0],
    division: queryParams.division[0],
    zone: queryParams.zone[0],
    search: premisesSearch,
    page: currentPage,
    limit: PAGE_SIZE,
    remoteUtilityId,
  });

  const premiselist = premiseList?.results;

  // ------------------ FORM FIELDS ---------------------
  const filterFields: FormField[] = [
    {
      name: "zone",
      label: "Zone",
      type: "multi-select",
      placeholder: "Select Zone",
      options: zoneList?.map((item: any) => ({
        value: item.id,
        label: item.name,
      })),
      className: "col-span-1",
    },
    {
      name: "division",
      label: "Division",
      type: "multi-select",
      placeholder: "Select Division",
      options: divisionList?.map((item: any) => ({
        value: item.id,
        label: item.name,
      })),
      className: "col-span-1",
      // disabled: !formFilterValues.zone || formFilterValues.zone.length === 0,
    },
    {
      name: "area",
      label: "Area",
      type: "multi-select",
      placeholder: "Select Area",
      options: areaList?.map((item: any) => ({
        value: item.id,
        label: item.name,
      })),
      className: "col-span-1",
      // disabled:
      //   !formFilterValues.division || formFilterValues.division.length === 0,
    },
    {
      name: "sub_area",
      label: "Sub Area",
      type: "multi-select",
      placeholder: "Select Sub Area",
      options: subAreaList?.map((item: any) => ({
        value: item.id,
        label: item.name,
      })),
      className: "col-span-1",
      // disabled: !formFilterValues.area || formFilterValues.area.length === 0,
    },
  ];

  // ------------------ FORM ---------------------
  const form = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
  });

  const watchedValues = form.watch();

  // ------------------ RESET DEPENDENT FIELDS ---------------------
  // Watch form changes for cascading dropdowns WITHOUT triggering API calls
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "zone") {
        form.setValue("division", []);
        form.setValue("area", []);
        form.setValue("sub_area", []);

        // Update form filter values for cascading (NOT query params)
        setFormFilterValues({
          zone: value.zone || [],
          division: [],
          area: [],
          sub_area: [],
        });
      }

      if (name === "division") {
        form.setValue("area", []);
        form.setValue("sub_area", []);

        // Update form filter values for cascading (NOT query params)
        setFormFilterValues((prev) => ({
          ...prev,
          division: value.division || [],
          area: [],
          sub_area: [],
        }));
      }

      if (name === "area") {
        form.setValue("sub_area", []);

        // Update form filter values for cascading (NOT query params)
        setFormFilterValues((prev) => ({
          ...prev,
          area: value.area || [],
          sub_area: [],
        }));
      }

      if (name === "sub_area") {
        // Update form filter values for cascading (NOT query params)
        setFormFilterValues((prev) => ({
          ...prev,
          sub_area: value.sub_area || [],
        }));
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  // ------------------ ON FILTER SUBMIT ---------------------
  // This is the ONLY place where queryParams is updated (triggers API call)
  const handleFilterSubmit = () => {
    const v = form.getValues();

    setCurrentPage(1);

    // Update query params only when Apply Filters is clicked
    setQueryParams({
      page: 1,
      search_data: premisesSearch.length > 2 ? premisesSearch : "",
      zone: v.zone || [],
      division: v.division || [],
      area: v.area || [],
      sub_area: v.sub_area || [],
    });
  };

  // ------------------ ON FILTER RESET ---------------------
  const handleFilterReset = () => {
    // Reset form values
    form.reset();

    // Reset form filter values
    setFormFilterValues({
      zone: [],
      division: [],
      area: [],
      sub_area: [],
    });

    // Reset query params (triggers API call with cleared filters)
    setCurrentPage(1);
    setQueryParams({
      page: 1,
      search_data: premisesSearch.length > 2 ? premisesSearch : "",
      zone: [],
      division: [],
      area: [],
      sub_area: [],
    });
  };

  // ------------------ SEARCH ---------------------
  const handleSearchChange = useCallback((search: string) => {
    setPremisesSearch(search);
    setCurrentPage(1);

    setQueryParams((prev) => ({
      ...prev,
      page: 1,
      search_data: search.length > 2 ? search : "",
    }));
  }, []);

  const handleSearch = useCallback(
    (searchTerm: string) => handleSearchChange(searchTerm),
    [handleSearchChange]
  );

  // ------------------ PAGINATION ---------------------
  const paginationConfig = useMemo(() => {
    if (!premiselist) return undefined;

    const totalItems = premiseList?.count || 0;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);

    return {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: (currentPage - 1) * PAGE_SIZE + 1,
      endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
    };
  }, [premiselist, currentPage, premiseList]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);

    setQueryParams((prev) => ({
      ...prev,
      page,
    }));
  };

  // ------------------ TABLE COLUMNS ---------------------
  const premisesColumns = [
    { key: "name", header: "Premise", sortable: true },
    {
      key: "configurationCode",
      header: "Code",
      render: (_, row) => row?.configurationCode,
    },
    {
      key: "area",
      header: "Area",
      render: (_, row) => row.hierarchyNames?.area,
    },
    {
      key: "subArea",
      header: "Sub Area",
      render: (_, row) => row.hierarchyNames?.subArea,
    },
    {
      key: "consumerCount",
      header: "Consumer Count",
      render: (value: number) => value?.toLocaleString(),
    },
    {
      key: "meterCount",
      header: "Meter Count",
      render: (value: number) => value?.toLocaleString(),
    },
  ];

  const [selectedId, setSelectedId] = useState<string | number | null>(null);
  // ------------------ DIALOG CYCLE TABLE COLUMNS ---------------------
  const cycleColumns: TableColumn[] = [
    {
      key: "id",
      header: "Select",
      render: (value) => (
        <input
          type="radio"
          name="row-selection"
          checked={selectedId === value}
          onChange={() => setSelectedId(value)}
          className="cursor-pointer"
        />
      ),
    },
    { key: "cycleName", header: "Cycle Name", sortable: true },
    { key: "category", header: "Category", sortable: true },
    { key: "subCategory", header: "Sub Category", sortable: true },
    { key: "premises", header: "Premises", sortable: true },
    { key: "consumers", header: "Consumers", sortable: true },
    { key: "meters", header: "Meters", sortable: true },
  ];

  // ------------------ PREMISE SELECTION HANDLER ---------------------
  const handleSelectionChange = (selectedIds: string[]) => {
    setSelectedPremises(selectedIds);
  };

  // ------------------ BUTTON HANDLERS ---------------------
  const handleMoveToPremise = () => {
    setIsDialogOpen(true);
    setSelectedCycle(null);
  };

  const handleAddPremise = () => {
    setIsAddDialogOpen(true)
  };

  const handleAddPremiseSubmit = () => {
    refetch();
    refetchDetail();
    setSelectedPremises([]);
  };

  const handleDialogConfirm = (cycleId) => {
    // Handle confirm action with selectedCycle
    console.log("Selected Cycle:", cycleId, selectedPremises);
    movePremiseToCycle(
      {
        remoteUtilityId,
        sourceBillCycleId: id,
        targetBillCycleId: cycleId,
        premises: selectedPremises,
      },
      {
        onSuccess: () => {
          refetchDetail();
          refetch();
          setSelectedPremises([]);
        },
      }
    );
    setIsDialogOpen(false);
  };

  const handleDialogCancel = () => {
    setIsDialogOpen(false);
    setSelectedCycle(null);
  };

  // ------------------ FILTER PANEL ---------------------
  const filters = (
    <div>
      <DynamicForm
        fields={filterFields}
        form={form}
        onSubmit={handleFilterSubmit}
        onReset={handleFilterReset}
        gridColumns={2}
        config={{
          layout: "vertical",
          showSubmitButton: true,
          showResetButton: true,
          submitButtonText: "Apply Filters",
          resetButtonText: "Clear",
          classes: {
            ...FIELD_CLASSES,
            buttonContainer: "flex justify-end gap-3",
          },
        }}
      />
    </div>
  );

  // ------------------ RETURN ---------------------
  return (
    <div>
      {/* Action Buttons */}
      <div className="mb-4 flex justify-end gap-3">
        <Button
          onClick={handleMoveToPremise}
          disabled={!canPerformAction || !selectedPremises || selectedPremises.length === 0}
        >
          Move Premise
        </Button>
        <Button onClick={handleAddPremise} disabled={!canPerformAction}>Add Premise</Button>
      </div>

      {/* Main Table */}
      <GenericTable
        data={premiselist ?? []}
        columns={premisesColumns}
        rowKey="code"
        selectable={true}
        selectedRows={selectedPremises}
        onSelectionChange={handleSelectionChange}
        searchPlaceholder="Search premises..."
        onSearch={handleSearch}
        emptyMessage="No premises match your search criteria"
        onPageChange={handlePageChange}
        pagination={paginationConfig}
        filterPanel={filters}
      />

      {/* Move to Premise Dialog */}
      <MoveToPremiseDialog
        isDialogOpen={isDialogOpen}
        setIsDialogOpen={setIsDialogOpen}
        onConfirm={(cycleId) => handleDialogConfirm(cycleId)}
      />

      <AddPremiseToCycle
        premises={premises}
        selectedCategories={selectedCategories}
        selectedSubCategories={selectedSubCategories}
        isDialogOpen={isAddDialogOpen}
        setIsDialogOpen={() => setIsAddDialogOpen(false)}
        onConfirm={() => handleAddPremiseSubmit()}
      />
    </div>
  );
};
